import { useCallback } from 'react';
import { PrimaryDataSet } from '../../types';
import { useProjectData } from '../stores/hooks/useProjectData';
import { restoreRows, clearAllExclusions } from '../utils/row-exclusion-manager';
import logger from '../utils/logger';

/**
 * 除外行の管理機能を提供するカスタムフック
 * 
 * ExcludedRowsTabから使用される除外行の復元機能を提供
 */
export const useExcludedRows = (dataset: PrimaryDataSet | undefined) => {
  const { updateDataset } = useProjectData();
  
  // 行を復元する
  const handleRestoreRows = useCallback((rowNumbers: readonly number[]) => {
    if (!dataset) {
      logger.warn('[useExcludedRows] Cannot restore rows: no dataset');
      return;
    }
    
    logger.info('[useExcludedRows] Restoring rows', { 
      datasetId: dataset.id, 
      rowCount: rowNumbers.length 
    });
    
    // 復元処理を実行
    restoreRows(dataset, rowNumbers);
    
    // ストアを更新（新しいオブジェクト参照を作成して再レンダリングをトリガー）
    updateDataset(dataset.id, () => ({ ...dataset }));
  }, [dataset, updateDataset]);
  
  // 全ての除外を解除する
  const handleClearAllExclusions = useCallback(() => {
    if (!dataset) {
      logger.warn('[useExcludedRows] Cannot clear exclusions: no dataset');
      return;
    }
    
    logger.info('[useExcludedRows] Clearing all exclusions', { 
      datasetId: dataset.id 
    });
    
    // 全解除処理を実行
    clearAllExclusions(dataset);
    
    // ストアを更新（新しいオブジェクト参照を作成して再レンダリングをトリガー）
    updateDataset(dataset.id, () => ({ ...dataset }));
  }, [dataset, updateDataset]);
  
  // 除外された行データを取得
  const excludedRows = dataset?.exclusions?.excludedRows
    ? Object.entries(dataset.exclusions.excludedRows).map(([rowNumber, excludedRow]) => ({
        rowNumber: Number(rowNumber),
        comment: excludedRow.comment,
        excludedAt: excludedRow.excludedAt,
        data: excludedRow.data
      }))
    : [];
  
  return {
    handleRestoreRows,
    handleClearAllExclusions,
    excludedRows
  };
};